// posts.js – React Query Hooks für Posts-Feature
import api from '../config/api';
import { useQuery, useMutation, useQueryClient } from 'react-query';

// Query Keys
export const postsKeys = {
    list: (params) => ['posts', 'list', params || {}],
    detail: (slug) => ['posts', 'detail', String(slug || '')],
    mine: () => ['posts', 'mine'],
    adminList: (params) => ['posts', 'admin', params || {}]
};

// Öffentliche Posts-Liste
export function usePostsList(params) {
    return useQuery(
        postsKeys.list(params),
        async () => {
            const res = await api.get('/api/posts', { params });
            return res.data;
        },
        { keepPreviousData: true, enabled: !!params }
    );
}

// Einzelner Post (öffentlich) – by slug
export function usePost(slug) {
    return useQuery(
        postsKeys.detail(slug),
        async () => {
            const res = await api.get(`/api/posts/${encodeURIComponent(slug)}`);
            return res.data?.post;
        },
        { enabled: !!slug }
    );
}

// Eigene Posts (auth)
export function useMyPosts() {
    return useQuery(
        postsKeys.mine(),
        async () => {
            const res = await api.get('/api/posts/_mine/list');
            return res.data?.posts || [];
        }
    );
}

// Post anlegen (auth, full)
export function useCreatePost() {
    const qc = useQueryClient();
    return useMutation(
        async (payload) => {
            const res = await api.post('/api/posts', payload);
            return res.data?.post;
        },
        {
            onSuccess: () => {
                qc.invalidateQueries(['posts']);
            }
        }
    );
}

// Post image upload (auth). Server returns { image_url }
export async function uploadMyPostImage(file) {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('image', file);
    const res = await fetch('/api/posts/_mine/upload-image', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
    });
    const data = await res.json();
    if (!res.ok || !data?.image_url) {
        const msg = data?.error?.message || data?.error || 'Ошибка при загрузке изображения';
        throw new Error(msg);
    }
    return data.image_url;
}

// Post bearbeiten (auth, full)
export function useUpdatePost() {
    const qc = useQueryClient();
    return useMutation(
        async ({ id, ...payload }) => {
            const res = await api.put(`/api/posts/${encodeURIComponent(id)}`, payload);
            return res.data?.post;
        },
        {
            onSuccess: (data, vars) => {
                const oldSlug = vars?.slug;
                const newSlug = data?.slug;
                if (oldSlug) qc.invalidateQueries(postsKeys.detail(oldSlug));
                if (newSlug) qc.invalidateQueries(postsKeys.detail(newSlug));
                qc.invalidateQueries(['posts']);
            }
        }
    );
}

// Post löschen (auth, full)
export function useDeletePost() {
    const qc = useQueryClient();
    return useMutation(
        async (vars) => {
            const id = typeof vars === 'string' ? vars : vars?.id;
            const slug = typeof vars === 'string' ? null : vars?.slug;
            await api.delete(`/api/posts/${encodeURIComponent(id)}`);
            return { id, slug };
        },
        {
            onSuccess: ({ slug }) => {
                if (slug) qc.invalidateQueries(postsKeys.detail(slug));
                qc.invalidateQueries(['posts']);
            }
        }
    );
}

// Admin: Posts-Liste (Moderation)
export function useAdminPosts(params) {
    return useQuery(
        postsKeys.adminList(params),
        async () => {
            const res = await api.get('/api/posts/_admin/list', { params });
            return res.data?.posts || [];
        },
        { enabled: !!params }
    );
}

// Admin: Status-Update
export function useAdminUpdatePostStatus() {
    const qc = useQueryClient();
    return useMutation(
        async ({ id, status }) => {
            const res = await api.put(`/api/posts/_admin/${encodeURIComponent(id)}/status`, { status });
            return res.data?.post;
        },
        {
            onSuccess: (data) => {
                if (data?.slug) qc.invalidateQueries(postsKeys.detail(data.slug));
                qc.invalidateQueries(postsKeys.adminList({}));
                qc.invalidateQueries(['posts']);
            }
        }
    );
}

// Admin: Delete
export function useAdminDeletePost() {
    const qc = useQueryClient();
    return useMutation(
        async ({ id, slug }) => {
            await api.delete(`/api/posts/_admin/${encodeURIComponent(id)}`);
            return { id, slug };
        },
        {
            onSuccess: ({ slug }) => {
                if (slug) qc.invalidateQueries(postsKeys.detail(slug));
                qc.invalidateQueries(postsKeys.adminList({}));
                qc.invalidateQueries(['posts']);
            }
        }
    );
}
